import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import SEOMeta from '@/components/base/SEOMeta';
import { articles, categories } from '@/mocks/articles';
import NotFound from '@/pages/NotFound';

export default function Category() {
  const { t } = useTranslation();
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const [activeSort, setActiveSort] = useState<'latest' | 'hot'>('latest');

  if (!category) {
    return <NotFound />;
  }

  const categoryArticles = articles
    .filter((a) => a.category === slug)
    .sort((a, b) => {
      if (activeSort === 'hot') return b.views - a.views;
      return new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime();
    });

  const categorySchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${category.name} - 黑料吃瓜`,
    description: category.description,
    url: `https://heiliao-chigua.com/category/${slug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '黑料吃瓜',
      url: 'https://heiliao-chigua.com',
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://heiliao-chigua.com/' },
      { '@type': 'ListItem', position: 2, name: category.name, item: `https://heiliao-chigua.com/category/${slug}` },
    ],
  };

  return (
    <>
      <SEOMeta
        title={category.name}
        description={`${category.description} - 黑料吃瓜为您聚合${category.name}最新内容，每日更新，吃瓜群众必备。`}
        keywords={`黑料吃瓜,${category.name},娱乐爆料,明星八卦,吃瓜`}
        ogTitle={`${category.name} - 黑料吃瓜`}
        ogDescription={`${category.description} - 黑料吃瓜为您聚合${category.name}最新内容。`}
        schema={[categorySchema, breadcrumbSchema]}
      />

      <ScrollToTop />
      <Navbar />

      <main className="pt-20">
        <div className="bg-background-100 border-b border-background-200/60">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <Breadcrumb
              items={[{ label: category.name }]}
            />
          </div>
        </div>

        <div className="w-full px-4 md:px-6 max-w-7xl mx-auto py-10 md:py-14">
          <div className="mb-10">
            <div className="flex items-center gap-4 mb-3">
              <div className="w-12 h-12 flex items-center justify-center rounded-2xl bg-primary-100 text-primary-600">
                <i className={`${category.icon} text-xl`}></i>
              </div>
              <div>
                <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
                  {category.name}
                </h1>
                <p className="text-sm text-foreground-500 mt-0.5">{category.description}</p>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 mb-8">
            <span className="text-sm text-foreground-500 mr-2">排序：</span>
            <button
              onClick={() => setActiveSort('latest')}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                activeSort === 'latest'
                  ? 'bg-primary-500 text-white'
                  : 'bg-background-100 text-foreground-600 hover:bg-background-200'
              }`}
            >
              最新发布
            </button>
            <button
              onClick={() => setActiveSort('hot')}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                activeSort === 'hot'
                  ? 'bg-primary-500 text-white'
                  : 'bg-background-100 text-foreground-600 hover:bg-background-200'
              }`}
            >
              最热爆料
            </button>
          </div>

          {categoryArticles.length > 0 ? (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {categoryArticles.map((article) => (
                  <ArticleCard key={article.id} {...article} />
                ))}
              </div>

              <div className="mt-10 text-center">
                <p className="text-sm text-foreground-500">
                  共 {categoryArticles.length} 篇{category.name}相关文章
                </p>
              </div>
            </>
          ) : (
            <div className="text-center py-20">
              <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                <i className="ri-inbox-line text-3xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-500">{t('noArticles')}</p>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </>
  );
}